/**
 * Report 2: Item Sales Statement (per-SKU sales + cost)
 *
 * One row per SKU sold in the chosen period. Shows revenue, COGS,
 * gross margin and margin %, plus a top-5 / bottom-5 margin breakdown.
 *
 * Data sources:
 *   - catalogMock.ts   → product master (SKU, name, standardCostCents)
 *   - invoicingMock.ts → invoice lines (period sales qty + revenue)
 */

import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowUpDown,
  BarChart3,
  ChevronUp,
  ChevronDown,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { DataTable, KpiCard } from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { printElement } from "../../../lib/export";
import {
  PeriodSelector,
  defaultPeriodWindow,
  type PeriodWindow,
} from "../../../components/gl/PeriodSelector";
import { useAppSettings } from "../../../lib/appSettings";

import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import { mockProducts, type MockProduct } from "../../../lib/catalogMock";
import {
  mockInvoices,
  mockInvoiceLines,
  type MockInvoice,
  type MockInvoiceLine,
} from "../../../lib/invoicingMock";

export const Route = createFileRoute("/_app/reports/item-sales-cost")({
  component: ItemSalesCostPage,
});

/* ─────────────────────────────────────────────────────────────
 * Types
 * ───────────────────────────────────────────────────────────── */

type SortKey =
  | "sku"
  | "name"
  | "unitsSold"
  | "revenue"
  | "cogs"
  | "grossMargin"
  | "marginPct"
  | "avgSellingPrice";

type ItemRow = {
  productId: string;
  sku: string;
  name: string;
  unitsSold: number;
  revenueCents: bigint;
  cogsCents: bigint | null;
  grossMarginCents: bigint | null;
  /** Margin % × 100 (e.g. 3450 = 34.50%). null when cogs unknown. */
  marginBp: number | null;
  avgSellingPriceCents: bigint;
};

/* ─────────────────────────────────────────────────────────────
 * Aggregation
 * ───────────────────────────────────────────────────────────── */

function computeItemSales(
  from: number,
  to: number,
  invoices: ReadonlyArray<MockInvoice>,
  invoiceLines: ReadonlyArray<MockInvoiceLine>,
): Map<string, { qty: number; revenueCents: bigint }> {
  const m = new Map<string, { qty: number; revenueCents: bigint }>();

  const validInvoiceIds = new Set(
    invoices
      .filter(
        (inv) =>
          inv.status !== "void" &&
          inv.status !== "draft" &&
          inv.issueDate >= from &&
          inv.issueDate <= to,
      )
      .map((inv) => inv._id),
  );

  for (const line of invoiceLines) {
    if (!validInvoiceIds.has(line.invoiceId)) continue;
    const prev = m.get(line.productId) ?? { qty: 0, revenueCents: 0n };
    m.set(line.productId, {
      qty: prev.qty + line.quantity,
      revenueCents: prev.revenueCents + line.lineTotalInBaseCents,
    });
  }
  return m;
}

/* ─────────────────────────────────────────────────────────────
 * Helpers
 * ───────────────────────────────────────────────────────────── */

function formatMarginPct(bp: number | null): string {
  if (bp === null) return "—";
  return `${(bp / 100).toFixed(1)}%`;
}

function marginColor(bp: number | null): string {
  if (bp === null) return "text-(--text-tertiary)";
  if (bp < 0) return "text-(--danger)";
  if (bp < 1000) return "text-(--warning-fg)";
  return "text-(--success-fg)";
}

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function ItemSalesCostPage() {
  const { companyProfile } = useAppSettings();

  const [catalogProducts] = usePersistentMockState<MockProduct[]>("products", mockProducts);
  const [invoices] = usePersistentMockState<MockInvoice[]>("invoices", mockInvoices);
  const [invoiceLines] = usePersistentMockState<MockInvoiceLine[]>("invoiceLines", mockInvoiceLines);

  const [period, setPeriod] = useState<PeriodWindow>(defaultPeriodWindow);
  const [sortKey, setSortKey] = useState<SortKey>("revenue");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const printRef = useRef<HTMLDivElement>(null);

  /* ─── Product cost lookup ─── */
  const costByCatalogProduct = useMemo(
    () =>
      new Map(
        catalogProducts.map((p) => [
          p._id,
          "standardCostCents" in p
            ? (p.standardCostCents as bigint)
            : null,
        ]),
      ),
    [catalogProducts],
  );

  const nameByProduct = useMemo(
    () => new Map(catalogProducts.map((p) => [p._id, p.displayName])),
    [catalogProducts],
  );

  const skuByProduct = useMemo(
    () => new Map(catalogProducts.map((p) => [p._id, p.sku])),
    [catalogProducts],
  );

  /* ─── Sales aggregation ─── */
  const salesMap = useMemo(
    () => computeItemSales(period.fromDate, period.toDate, invoices, invoiceLines),
    [period, invoices, invoiceLines],
  );

  /* ─── Build rows ─── */
  const allRows = useMemo<ItemRow[]>(() => {
    const rows: ItemRow[] = [];
    for (const [productId, sales] of salesMap) {
      if (sales.qty === 0) continue;
      const sku = skuByProduct.get(productId) ?? productId;
      const name = nameByProduct.get(productId) ?? productId;
      const unitCost = costByCatalogProduct.get(productId) ?? null;
      const cogsCents =
        unitCost != null ? BigInt(sales.qty) * unitCost : null;
      const grossMarginCents =
        cogsCents != null ? sales.revenueCents - cogsCents : null;
      const marginBp: number | null =
        grossMarginCents != null && sales.revenueCents > 0n
          ? Number((grossMarginCents * 10000n) / sales.revenueCents)
          : null;
      const avgSellingPriceCents =
        sales.qty > 0
          ? sales.revenueCents / BigInt(sales.qty)
          : 0n;

      rows.push({
        productId,
        sku,
        name,
        unitsSold: sales.qty,
        revenueCents: sales.revenueCents,
        cogsCents,
        grossMarginCents,
        marginBp,
        avgSellingPriceCents,
      });
    }
    return rows;
  }, [salesMap, skuByProduct, nameByProduct, costByCatalogProduct]);

  /* ─── Sorted rows ─── */
  const rows = useMemo(() => {
    return [...allRows].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "sku":
          cmp = a.sku.localeCompare(b.sku);
          break;
        case "name":
          cmp = a.name.localeCompare(b.name);
          break;
        case "unitsSold":
          cmp = a.unitsSold - b.unitsSold;
          break;
        case "revenue":
          cmp =
            a.revenueCents < b.revenueCents
              ? -1
              : a.revenueCents > b.revenueCents
                ? 1
                : 0;
          break;
        case "cogs":
          cmp =
            (a.cogsCents ?? -1n) < (b.cogsCents ?? -1n)
              ? -1
              : (a.cogsCents ?? -1n) > (b.cogsCents ?? -1n)
                ? 1
                : 0;
          break;
        case "grossMargin":
          cmp =
            (a.grossMarginCents ?? 0n) < (b.grossMarginCents ?? 0n)
              ? -1
              : (a.grossMarginCents ?? 0n) > (b.grossMarginCents ?? 0n)
                ? 1
                : 0;
          break;
        case "marginPct":
          cmp = (a.marginBp ?? 0) - (b.marginBp ?? 0);
          break;
        case "avgSellingPrice":
          cmp =
            a.avgSellingPriceCents < b.avgSellingPriceCents
              ? -1
              : a.avgSellingPriceCents > b.avgSellingPriceCents
                ? 1
                : 0;
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [allRows, sortKey, sortDir]);

  /* ─── Totals ─── */
  const totals = useMemo(() => {
    let totalRevenue = 0n;
    let totalCogs = 0n;
    let hasCogs = false;
    for (const r of rows) {
      totalRevenue += r.revenueCents;
      if (r.cogsCents != null) {
        totalCogs += r.cogsCents;
        hasCogs = true;
      }
    }
    const grossMargin = hasCogs ? totalRevenue - totalCogs : null;
    const marginBp =
      grossMargin != null && totalRevenue > 0n
        ? Number((grossMargin * 10000n) / totalRevenue)
        : null;
    return { totalRevenue, totalCogs, hasCogs, grossMargin, marginBp };
  }, [rows]);

  /* ─── KPI: best margin % ─── */
  const bestMarginRow = useMemo(
    () =>
      allRows.reduce<ItemRow | null>(
        (best, r) =>
          r.marginBp !== null &&
          (best === null || r.marginBp > (best.marginBp ?? 0))
            ? r
            : best,
        null,
      ),
    [allRows],
  );

  /* ─── Top 5 / Bottom 5 by margin % ─── */
  const rowsWithMargin = useMemo(
    () => rows.filter((r) => r.marginBp !== null),
    [rows],
  );

  const top5 = useMemo(
    () =>
      [...rowsWithMargin]
        .sort((a, b) => (b.marginBp ?? 0) - (a.marginBp ?? 0))
        .slice(0, 5),
    [rowsWithMargin],
  );

  const bottom5 = useMemo(
    () =>
      [...rowsWithMargin]
        .sort((a, b) => (a.marginBp ?? 0) - (b.marginBp ?? 0))
        .slice(0, 5),
    [rowsWithMargin],
  );

  /* ─── Sort toggle ─── */
  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col)
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-30" />;
    return sortDir === "asc" ? (
      <ChevronUp className="ml-1 h-3 w-3" />
    ) : (
      <ChevronDown className="ml-1 h-3 w-3" />
    );
  }

  /* ─── Export ─── */
  type CsvRow = {
    sku: string;
    name: string;
    unitsSold: number;
    revenueCents: bigint;
    cogsCents: string;
    grossMarginCents: string;
    marginPct: string;
    avgSellingPrice: string;
  };

  function buildCsvRows(): CsvRow[] {
    const data: CsvRow[] = rows.map((r) => ({
      sku: r.sku,
      name: r.name,
      unitsSold: r.unitsSold,
      revenueCents: r.revenueCents,
      cogsCents:
        r.cogsCents != null
          ? (Number(r.cogsCents) / 100).toFixed(2)
          : "—",
      grossMarginCents:
        r.grossMarginCents != null
          ? (Number(r.grossMarginCents) / 100).toFixed(2)
          : "—",
      marginPct: formatMarginPct(r.marginBp),
      avgSellingPrice: (Number(r.avgSellingPriceCents) / 100).toFixed(2),
    }));
    // Footer total row.
    data.push({
      sku: "TOTAL",
      name: "",
      unitsSold: rows.reduce((s, r) => s + r.unitsSold, 0),
      revenueCents: totals.totalRevenue,
      cogsCents: totals.hasCogs
        ? (Number(totals.totalCogs) / 100).toFixed(2)
        : "—",
      grossMarginCents: totals.grossMargin != null
        ? (Number(totals.grossMargin) / 100).toFixed(2)
        : "—",
      marginPct: formatMarginPct(totals.marginBp),
      avgSellingPrice: "",
    });
    return data;
  }

  const CSV_COLUMNS: ExportColumn<CsvRow>[] = [
    { key: "sku", label: "SKU" },
    { key: "name", label: "Product" },
    {
      key: "unitsSold",
      label: "Units Sold",
      format: (v) => String(v),
    },
    { key: "revenueCents", label: "Revenue (BDT)" },
    {
      key: "cogsCents",
      label: "COGS (BDT)",
      format: (v) => String(v),
    },
    {
      key: "grossMarginCents",
      label: "Gross Margin (BDT)",
      format: (v) => String(v),
    },
    {
      key: "marginPct",
      label: "Margin %",
      format: (v) => String(v),
    },
    {
      key: "avgSellingPrice",
      label: "Avg Selling Price (BDT)",
      format: (v) => String(v),
    },
  ];

  function handleCsv(ext: "csv" | "xls") {
    downloadCsv(
      exportFilename("item-sales-cost", ext),
      toCsv(buildCsvRows(), CSV_COLUMNS),
    );
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: "Item Sales Statement",
      company: companyProfile.displayName,
      subtitle: `Period: ${new Date(period.fromDate).toLocaleDateString("en-BD")} – ${new Date(period.toDate).toLocaleDateString("en-BD")}`,
    });
  }

  /* ─── Table columns ─── */
  const columns = useMemo<ColumnDef<ItemRow>[]>(
    () => [
      {
        id: "sku",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("sku")}
          >
            SKU <SortIcon col="sku" />
          </button>
        ),
        cell: ({ row }) => (
          <code className="font-mono text-[12px] text-(--text-secondary) bg-(--bg-hover) rounded px-1.5 py-0.5">
            {row.original.sku}
          </code>
        ),
      },
      {
        id: "name",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("name")}
          >
            Product <SortIcon col="name" />
          </button>
        ),
        cell: ({ row }) => (
          <span className="text-(--text-primary) font-medium">
            {row.original.name}
          </span>
        ),
      },
      {
        id: "unitsSold",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("unitsSold")}
          >
            Units Sold <SortIcon col="unitsSold" />
          </button>
        ),
        meta: { numeric: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {row.original.unitsSold.toLocaleString("en-BD")}
          </span>
        ),
      },
      {
        id: "revenue",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("revenue")}
          >
            Revenue <SortIcon col="revenue" />
          </button>
        ),
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums font-semibold">
            {formatMoney(row.original.revenueCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
      },
      {
        id: "cogs",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("cogs")}
          >
            COGS <SortIcon col="cogs" />
          </button>
        ),
        meta: { numeric: true, mono: true },
        cell: ({ row }) =>
          row.original.cogsCents != null ? (
            <span className="font-mono tabular-nums">
              {formatMoney(row.original.cogsCents, "BDT", {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "grossMargin",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("grossMargin")}
          >
            Gross Margin <SortIcon col="grossMargin" />
          </button>
        ),
        meta: { numeric: true, mono: true },
        cell: ({ row }) =>
          row.original.grossMarginCents != null ? (
            <span
              className={`font-mono tabular-nums ${
                row.original.grossMarginCents < 0n
                  ? "text-(--danger)"
                  : "text-(--text-primary)"
              }`}
            >
              {formatMoney(row.original.grossMarginCents, "BDT", {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "marginPct",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("marginPct")}
          >
            Margin % <SortIcon col="marginPct" />
          </button>
        ),
        meta: { numeric: true },
        cell: ({ row }) => (
          <span
            className={`font-mono tabular-nums font-semibold ${marginColor(row.original.marginBp)}`}
          >
            {formatMarginPct(row.original.marginBp)}
          </span>
        ),
      },
      {
        id: "avgSellingPrice",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("avgSellingPrice")}
          >
            Avg Price <SortIcon col="avgSellingPrice" />
          </button>
        ),
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {formatMoney(row.original.avgSellingPriceCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortKey, sortDir],
  );

  return (
    <div className="flex flex-col gap-6" ref={printRef}>
      <PageHeader
        title="Item Sales Statement"
        description="Per-SKU sales, cost and margin for the selected period."
        actions={
          <ExportButton
            onCsv={() => handleCsv("csv")}
            onExcel={() => handleCsv("xls")}
            onPrint={handlePrint}
          />
        }
      />

      {/* KPI strip */}
      <section
        className="grid grid-cols-2 sm:grid-cols-4 gap-4"
        aria-label="Sales & margin KPIs"
      >
        <KpiCard
          caption="Total Revenue"
          value={formatMoneySmart(totals.totalRevenue, "BDT", {
            locale: "en-BD",
          })}
          hint="period"
        />
        <KpiCard
          caption="Total COGS"
          value={
            totals.hasCogs
              ? formatMoneySmart(totals.totalCogs, "BDT", {
                  locale: "en-BD",
                })
              : "—"
          }
          hint="period"
        />
        <KpiCard
          caption="Gross Margin"
          value={
            totals.grossMargin != null
              ? formatMoneySmart(totals.grossMargin, "BDT", {
                  locale: "en-BD",
                })
              : "—"
          }
          hint={totals.marginBp != null ? formatMarginPct(totals.marginBp) : ""}
        />
        <KpiCard
          caption="Best Margin %"
          value={formatMarginPct(bestMarginRow?.marginBp ?? null)}
          hint={bestMarginRow?.name ?? ""}
          icon={<TrendingUp className="h-4 w-4" aria-hidden />}
        />
      </section>

      {/* Period selector */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] uppercase tracking-wider text-(--text-tertiary)">
          Period
        </span>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* Main table */}
      <DataTable
        data={rows}
        columns={columns}
        getRowId={(r) => r.productId}
        emptyTitle="No sales in this period"
        emptyDescription="Adjust the period selector to see sales data."
        emptyIcon={<BarChart3 className="h-5 w-5" aria-hidden />}
      />

      {/* Aggregate footer row */}
      <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) p-3 text-[13px]">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div>
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">
              SKUs sold
            </div>
            <div className="font-mono tabular-nums font-semibold text-(--text-primary)">
              {rows.length}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">
              Total Revenue
            </div>
            <div className="font-mono tabular-nums font-semibold text-(--text-primary)">
              {formatMoney(totals.totalRevenue, "BDT", { locale: "en-BD" })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">
              Total COGS
            </div>
            <div className="font-mono tabular-nums font-semibold text-(--text-primary)">
              {totals.hasCogs
                ? formatMoney(totals.totalCogs, "BDT", { locale: "en-BD" })
                : "—"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">
              Gross Margin
            </div>
            <div
              className={`font-mono tabular-nums font-semibold ${totals.grossMargin != null && totals.grossMargin < 0n ? "text-(--danger)" : "text-(--text-primary)"}`}
            >
              {totals.grossMargin != null
                ? formatMoney(totals.grossMargin, "BDT", { locale: "en-BD" })
                : "—"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">
              Margin %
            </div>
            <div
              className={`font-mono tabular-nums font-semibold ${marginColor(totals.marginBp)}`}
            >
              {formatMarginPct(totals.marginBp)}
            </div>
          </div>
        </div>
      </div>

      {/* Top 5 / Bottom 5 section */}
      {rowsWithMargin.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Top 5 — winners */}
          <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-(--border-subtle) bg-(--bg-hover)">
              <TrendingUp className="h-3.5 w-3.5 text-(--success-fg)" aria-hidden />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-(--text-secondary)">
                Top 5 by Margin %
              </span>
            </div>
            <div className="divide-y divide-(--border-subtle)">
              {top5.map((r, i) => (
                <MarginRow key={r.productId} rank={i + 1} row={r} />
              ))}
              {top5.length === 0 && (
                <p className="p-4 text-[12px] text-(--text-tertiary)">
                  No margin data available.
                </p>
              )}
            </div>
          </div>

          {/* Bottom 5 — losers */}
          <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-(--border-subtle) bg-(--bg-hover)">
              <TrendingDown className="h-3.5 w-3.5 text-(--danger)" aria-hidden />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-(--text-secondary)">
                Bottom 5 by Margin %
              </span>
            </div>
            <div className="divide-y divide-(--border-subtle)">
              {bottom5.map((r, i) => (
                <MarginRow key={r.productId} rank={i + 1} row={r} />
              ))}
              {bottom5.length === 0 && (
                <p className="p-4 text-[12px] text-(--text-tertiary)">
                  No margin data available.
                </p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * MarginRow — compact row used in the top/bottom 5 panels
 * ───────────────────────────────────────────────────────────── */

function MarginRow({
  rank,
  row,
}: {
  rank: number;
  row: ItemRow;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <span className="w-5 text-[11px] font-mono text-(--text-tertiary) shrink-0">
        {rank}.
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-medium text-(--text-primary) truncate">
          {row.name}
        </div>
        <div className="text-[11px] text-(--text-tertiary)">
          <code className="font-mono">{row.sku}</code>
          {" · "}
          {row.unitsSold.toLocaleString("en-BD")} units
        </div>
      </div>
      <div className="text-right shrink-0">
        <div
          className={`text-[13px] font-mono font-semibold ${marginColor(row.marginBp)}`}
        >
          {formatMarginPct(row.marginBp)}
        </div>
        <div className="text-[11px] font-mono text-(--text-tertiary)">
          {row.grossMarginCents != null
            ? formatMoneySmart(row.grossMarginCents, "BDT", { locale: "en-BD" })
            : "—"}
        </div>
      </div>
    </div>
  );
}
